import React, { useEffect, useRef, useState } from "react";
import { Platform, ScrollView, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  CTA,
  FlexGrid,
  Heading,
  Text,
  formatCurrency,
  useResponsive,
  Button,
  Panel,
  theme,
} from "@clearabee/ui-library-native";
import NextLink from "next/link";
import {
  useCatalogue,
  getItemDescendants,
  getCalloutCharge,
  instance,
  readAddresses,
} from "@clearabee/ui-sdk";
import { Anchor } from "components/Core/Anchor/Anchor";
import { Layout } from "components/Layout/Layout";
import { SEO } from "components/Seo/Seo";
import { requirePostcodePrice } from "lib/utils";
import { useAnalytics } from "lib/hooks/useAnalytics";
import { useRouter } from "lib/hooks/useRouter";
import { styles, ids } from "./SelectProduct.styles";
import placeholderImage from "lib/images/placeholder.png";
import { CatalogueItem } from "lib/types";
import { Image } from "components/Image/Image";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import {
  PostcodePricing,
  Values,
} from "components/PostcodePricing/PostcodePricing";
import { PostcodeResultMessage } from "components/PostcodeResultMessage/PostcodeResultMessage";
import { useMutation } from "react-query";
import { Spinner } from "components/Core/Spinner/Spinner";
import { ManVanCard } from "components/Cards/ManVanCard";
import greenTickImage from "../../lib/images/green-tick.png";

interface SelectProductProps {
  categorySku: string;
  postcode?: string;
  wasteType?: string;
}

const sortByPriceAndSize = (items: CatalogueItem[]): CatalogueItem[] => {
  return items.sort(
    (a, b) =>
      Number(a.meta?.yards ?? a.price ?? 0) -
      Number(b.meta?.yards ?? b.price ?? 0),
  );
};

// this content is only for MANVAN
const payAsYouGoContents = [
  "Only pay for what we collect",
  "No call out fee on returns up to an XX Large",
  "Only pay mimimum charge today (includes a mini load)",
  "Option to add extra units now or pay on the day",
] as const;

export const SelectProduct = ({
  categorySku,
  postcode: postcodeParam,
  wasteType: wasteTypeParam,
}: SelectProductProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { ecommerce } = useAnalytics();
  const { replace, navigate } = useRouter();
  const { defaultCatalogue, setCatalogue, getCatalogue } = useCatalogue();
  const [wasteType, setWasteType] = useState(wasteTypeParam ?? "");
  const [postcode, setPostcode] = useState(postcodeParam ?? "");
  const [invalidPostcode, setInvalidPostcode] = useState(false);
  const catalogue = getCatalogue(postcode) || defaultCatalogue;
  const scrollRef = useRef<ScrollView>(null);
  const service = catalogue.items.find(({ sku }) => sku === categorySku);
  const { screens } = useResponsive();
  const analyticData = {
    item_list_name: `${categorySku} Product List`,
    item_list_id: `${categorySku}_product_list`,
  };
  const { isLoading, mutate: getPostcodeCatalogue } = useMutation(
    (postcode: string) =>
      instance.catalogues.getCatalogue("hwa", {
        params: { postcode, flatten: true },
      }),

    {
      onSuccess: (response, mutatePostcode) => {
        setCatalogue(mutatePostcode, response.data);
        scrollRef.current?.scrollTo({ y: 650, animated: true });
      },
    },
  );

  const products = sortByPriceAndSize(
    getItemDescendants(categorySku, catalogue.items).filter(
      ({ price, meta }) =>
        isLoading ||
        invalidPostcode ||
        !postcode ||
        (price &&
          (categorySku !== "SKIPS" ||
            !(
              ["Soil", "HeavyConstruction"].includes(wasteType ?? "") &&
              parseInt(meta?.yards) > 8
            ))),
    ),
  );

  const isMANVAN = categorySku === "MANVAN";

  const flexLoadProduct = catalogue.items.find(
    (product) => product.sku === "FLEXLOAD",
  );
  const calloutCharge =
    catalogue.items.find(({ sku }) => sku.match(/callout/gi))?.price || 0;

  const requirePostcodePrices =
    requirePostcodePrice(products) || !products.length || isLoading;

  const isExtended = products.length >= 5;

  const isDisabled =
    isLoading ||
    (requirePostcodePrices && !postcode) ||
    (categorySku === "SKIPS" && !wasteType) ||
    invalidPostcode;

  /*
   * Make sure we show MANVAN's Flexible Load as the last product
   */
  if (categorySku === "MANVAN") {
    const flexLoadIndex = products.findIndex(({ sku }) =>
      sku.includes("FLEXLOAD"),
    );

    // remove the flex load product from the list
    if (flexLoadIndex !== -1) {
      products.splice(flexLoadIndex, 1);
    }
  }

  const handleSubmit = async ({ postcode, wasteType }: Values) => {
    try {
      const { addresses } = await readAddresses(postcode);
      if (addresses.length) {
        setInvalidPostcode(false);
        setPostcode(postcode);
        if (!getCatalogue(postcode)) {
          getPostcodeCatalogue(postcode);
        }
      } else {
        setInvalidPostcode(true);
        setPostcode("");
      }
    } catch (e) {
      setInvalidPostcode(true);
      setPostcode("");
    }

    setWasteType(wasteType ?? "");

    const route = {
      routeName: "ServicePage",
      params: {
        categorySku,
        postcode: postcode || undefined,
        wasteType: wasteType || undefined,
      },
    } as const;

    if (Platform.OS === "web") {
      replace(route);
    } else {
      navigate(route);
    }
  };

  useEffect(() => {
    ecommerce.viewItemList(
      products,
      analyticData.item_list_name,
      analyticData.item_list_id,
    );
  }, []);

  if (!service) {
    return <ErrorPage />;
  }

  return (
    <>
      <SEO
        title={`${translate("hwa.screens.selectProduct.title")} - ${
          service?.title ?? categorySku
        }`}
      />
      <Layout
        title={translate("hwa.screens.selectProduct.title")}
        backTo={{ routeName: "Book" }}
        contentWidth={isExtended ? "xlarge3" : "xlarge"}
        scrollRef={scrollRef}
      >
        {isMANVAN && !!flexLoadProduct && (
          <>
            {/* PAY AS YOU GO PRODUCT SECTION */}
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: screens.xlarge ? "flex-start" : "center",
              }}
            >
              <Heading level={4} color="accent">
                Pay as you go
              </Heading>
            </div>
            <Panel
              styles={{
                backgroundColor: theme.colors.brand.darkest,
                width: "100%",
                display: "flex",
                flexDirection: screens.xlarge ? "row" : "column",
                justifyContent: screens.xlarge ? "space-between" : "center",
                alignItems: "center",
                gap: theme.spacing.medium,
                marginHorizontal: "auto",
                marginTop: theme.spacing.xsmall,
                marginBottom: theme.spacing.xlarge,
                ...(screens.medium && {
                  width: "75%",
                }),
                ...(screens.xlarge && {
                  width: "100%",
                }),
              }}
            >
              <img
                src={flexLoadProduct?.img || placeholderImage}
                width={screens.xlarge ? 250 : 200}
                height={screens.xlarge ? 250 : 200}
                alt="flexloadImage"
                loading="lazy"
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: screens.xlarge ? "row" : "column",
                  justifyContent: screens.xlarge ? "space-between" : "center",
                  alignItems: screens.xlarge ? "start" : "center",
                  marginTop: theme.spacing.xlarge,
                }}
              >
                <div
                  style={{
                    marginBottom: theme.spacing.xsmall,
                  }}
                >
                  {payAsYouGoContents.map((content, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: theme.spacing.xsmall,
                        marginBottom: theme.spacing.small,
                      }}
                    >
                      <img
                        src={greenTickImage}
                        width={25}
                        height={25}
                        alt="greenTickImage"
                        loading="lazy"
                      />
                      <Text color="light" fontSize="small">
                        {content}
                      </Text>
                    </div>
                  ))}
                </div>

                {screens.xlarge && (
                  <div
                    style={{
                      paddingLeft: theme.spacing.medium,
                      paddingRight: theme.spacing.xlarge,
                    }}
                  >
                    <div
                      style={{
                        transform: "rotate(15deg)",
                        borderRightStyle: "solid",
                        borderBottom: "2px",
                        borderRightWidth: "1px",
                        borderRightColor: theme.colors.greyscale.lightest,
                        display: "flex",
                        flexDirection: "column",
                        height: "180px",
                        opacity: 0.25,
                      }}
                    />
                  </div>
                )}

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.spacing.small,
                    marginBottom: theme.spacing.large,
                    ...(screens.small && {
                      width: "80%",
                    }),
                    ...(screens.medium && {
                      width: "66.66%",
                    }),
                    ...(screens.xlarge && {
                      width: "33.33%",
                    }),
                  }}
                >
                  <Heading level={3} color="warning">
                    {flexLoadProduct?.title}
                  </Heading>
                  <Text
                    color="light"
                    styles={{
                      fontWeight: "bold",
                    }}
                    fontSize="large"
                  >{`${formatCurrency(
                    (flexLoadProduct?.price ?? 0) + calloutCharge,
                  )} Inc VAT`}</Text>
                  <Text color="light" fontSize="small">
                    {flexLoadProduct?.description}
                  </Text>
                </div>
              </div>

              <div
                style={{
                  width: "25%",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <NextLink
                  legacyBehavior
                  href={`/book/MANVAN/${flexLoadProduct?.sku}`}
                  passHref
                >
                  <Button color="warning" size="large">
                    Book Now
                  </Button>
                </NextLink>
              </div>
            </Panel>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: screens.xlarge ? "flex-start" : "center",
                marginBottom: theme.spacing.medium,
              }}
            >
              <Heading level={4} color="accent">
                Fixed loads
              </Heading>
            </div>
          </>
        )}

        {requirePostcodePrices && (
          <View style={{ zIndex: 1 }}>
            <PostcodePricing
              postcode={postcode}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              isExtended={isExtended}
              categorySku={categorySku}
              wasteType={wasteTypeParam}
            />
            <PostcodeResultMessage
              postcode={postcode}
              hasResults={!!products.length}
              invalidPostcode={invalidPostcode}
            />
          </View>
        )}

        <FlexGrid
          cellSpacing={screens.medium ? "xsmall" : "xsmall2"}
          rowSpacing={screens.medium ? "xsmall" : "xsmall2"}
          wrap="wrap"
          style={styles.flexGrid}
          dataSet={{ media: ids.flexGrid }}
        >
          {products.map((product) => {
            const { title, price, img, meta, sku, description } = product;
            return (
              <FlexGrid.Cell
                key={title}
                base="100%"
                xsmall="50%"
                medium="33.33%"
                large="33.33%"
                xlarge="25%"
              >
                <Anchor
                  styles={{ flex: 1 }}
                  routeName="ProductPage"
                  params={{
                    categorySku,
                    productSku: sku,
                    postcode: requirePostcodePrices ? postcode : undefined,
                    wasteType: categorySku === "SKIPS" ? wasteType : undefined,
                  }}
                  disabled={isDisabled}
                  onClick={() => {
                    ecommerce.selectItem(
                      product,
                      analyticData.item_list_name,
                      analyticData.item_list_id,
                    );
                  }}
                >
                  <CTA
                    orientation="portrait"
                    shadow={Platform.OS === "web"}
                    heading={
                      <Heading
                        level={4}
                        styles={styles.CTAHeading}
                        dataSet={{ media: ids.CTAHeading }}
                        numberOfLines={screens.medium ? 1 : undefined}
                      >
                        {title}
                      </Heading>
                    }
                    subheading={
                      <View style={styles.subheadingContainer}>
                        {isLoading && <Spinner color="brand" />}
                        {!isLoading && !!price && (
                          <Text
                            styles={styles.subheading}
                            dataSet={{ media: ids.subheading }}
                          >
                            {requirePostcodePrices && !postcode && (
                              <Text>{translate("common.from")} </Text>
                            )}
                            {formatCurrency(
                              product.noCalloutCharge
                                ? price
                                : price +
                                    getCalloutCharge(catalogue.items, service),
                            )}
                            <Text
                              styles={{
                                fontWeight: "100",
                              }}
                            >
                              {" "}
                              {translate("common.incVat")}
                            </Text>
                          </Text>
                        )}
                      </View>
                    }
                    textColor="brand"
                    image={
                      <Image
                        source={img ?? placeholderImage}
                        resizeMode="contain"
                        style={styles.CTAImage}
                      />
                    }
                    pill={
                      meta?.popular
                        ? translate("common.mostPopular")
                        : undefined
                    }
                    background="light"
                    styles={styles.CTA}
                    dataSet={{ media: ids.CTA }}
                  >
                    {screens.medium && (
                      <>
                        <Text
                          fontSize="xsmall"
                          styles={styles.descriptionText}
                          dataSet={{ media: ids.descriptionText }}
                        >
                          {description}
                        </Text>
                        <Button
                          color="warning"
                          size="medium"
                          styles={styles.button}
                          disabled={isDisabled}
                        >
                          <Text
                            fontSize="small"
                            styles={{ fontFamily: "ProximaSemiBold" }}
                          >
                            {translate("common.bookNow")}
                          </Text>
                        </Button>
                      </>
                    )}
                    {!screens.medium && !!description && (
                      <Text
                        fontSize="xsmall"
                        styles={styles.mobileDescription}
                        dataSet={{ media: ids.mobileDescription }}
                      >
                        {description}
                      </Text>
                    )}
                  </CTA>
                </Anchor>
              </FlexGrid.Cell>
            );
          })}
          {!isMANVAN && (
            <FlexGrid.Cell
              base="100%"
              medium={undefined}
              large="33.33%"
              xlarge="20%"
            >
              <ManVanCard />
            </FlexGrid.Cell>
          )}
        </FlexGrid>
      </Layout>
    </>
  );
};
